/**
 * The one recovery behind `SdkRequestError.mayHaveStored`'s promise: every
 * checked write of the SDK that failed calls `afterFailedWrite` from its
 * catch, after its take-back. When the failure may have stored the write
 * (no verdict came back, or a 2xx the SDK could not read) the write's
 * recovery runs — a re-read of what it wrote, or its cache marked stale and
 * its readers told — so a write that did land comes back from the server
 * instead of staying taken back. A refusal (nothing stored) and a request
 * never sent (`"unaddressable"`) run nothing. A hygiene check of the
 * repository fails a checked write's catch that takes a write back without
 * calling it.
 *
 * @internal SDK-private: no barrel exports it.
 */
import type { Option } from "@tento-lona/sheets";
import { SdkRequestError } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";

/**
 * After a checked write failed with `err`: when the server may have stored
 * it (`SdkRequestError.mayHaveStored`), run `recover` — the write's re-read,
 * or its cache marked stale and its readers told. `owner` is the lifecycle
 * the write belongs to (the client's, the row's sheet's, the row's): once
 * it was disposed no recovery starts (a disposed owner does no work);
 * `null` for a write nothing owns. `what` names the write in the trace a
 * failed recovery leaves. Never throws and never rejects: a recovery that
 * throws, or whose promise rejects, is traced and leaves the cache as the
 * take-back left it. The failed write rejects with its own error either
 * way — the caller rethrows `err` after this returns.
 */
export function afterFailedWrite(
  err: unknown,
  owner: Option<AfterFailedWrite.Owner>,
  what: string,
  recover: AfterFailedWrite.Recover,
): void {
  if (!SdkRequestError.mayHaveStored(err)) return;
  if (owner != null && !AfterFailedWrite.isLive(owner)) return;
  const traced = (failure: unknown): void =>
    Tracing.warn("net")(`${what}: the recovery after a write that may have stored failed: ${String(failure)}`);
  try {
    const running = recover();
    if (running != null) void running.catch(traced);
  } catch (failure) {
    traced(failure);
  }
}

export namespace AfterFailedWrite {
  /**
   * The lifecycle a write belongs to: `assertLive` throws once it was
   * disposed (`LonaSdk.Client.Lifecycle`, a sheet's or a row's
   * `CellHandle.Owner`).
   */
  export interface Owner {
    assertLive(verb: string): void;
  }

  /**
   * A write's recovery: synchronous (mark the cache stale and tell its
   * readers — `undefined`) or a re-read (its promise; an answer that
   * arrives after the owner was disposed is the re-read's to drop).
   */
  export type Recover = () => Promise<void> | undefined;

  /** Whether `owner` is still live (its `assertLive` does not throw). */
  export function isLive(owner: Owner): boolean {
    try {
      owner.assertLive("afterFailedWrite");
      return true;
    } catch (_disposed) {
      return false;
    }
  }
}
